"use client";

import dynamic from "next/dynamic";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import type { Locale } from "@/lib/i18n/config";
import {
	AD_WALL_SURFACE,
	type AdWallPage,
	type AdWallTrigger,
	claimEligibleThisVisit,
	countPageViewSinceWall,
	isLandingPage,
	markWallShown,
	wallDue,
	wallOpensFor,
	wallsShownThisVisit,
} from "@/lib/monetization/ad-wall";
import { useAdGuardrails } from "@/lib/monetization/use-ad-guardrails";
import { useAdWallArm } from "@/lib/monetization/use-ad-wall-arm";
import { useAdWallIntensity } from "@/lib/monetization/use-ad-wall-intensity";
import { APPLY_CLICKED_EVENT } from "./post-apply-offer";

// Client-only and loaded on demand: visitors who never see the wall never
// download it.
const AdWallDialog = dynamic(
	() => import("./ad-wall-dialog").then((m) => m.AdWallDialog),
	{ ssr: false },
);

function landingNow(): boolean {
	const nav = performance.getEntriesByType?.("navigation")[0] as
		| PerformanceNavigationTiming
		| undefined;
	return isLandingPage({
		href: window.location.href,
		navigationUrl: nav?.name ?? null,
		referrer: document.referrer,
		origin: window.location.origin,
	});
}

/**
 * Mount point for the H7 ad wall (`ad-wall-placement`), crossed with H8
 * (`ad-wall-intensity`). Renders nothing until the wall opens; decides whether
 * this page view is eligible, whether this visitor's placement arm opens the
 * wall here (on mount) or on the next apply click, and whether the intensity
 * arm says a wall is due (the visit's first, or a capped repeat every Nth
 * page view). Each mount of a host counts as one in-site page view.
 */
export function AdWallHost({
	page,
	locale,
	jobId = null,
	categoryId = null,
}: {
	page: AdWallPage;
	locale: Locale;
	jobId?: string | null;
	categoryId?: string | null;
}) {
	const posthog = usePostHog();
	const { ready, arm } = useAdWallArm();
	const intensityState = useAdWallIntensity();
	const { intensity, config } = intensityState;
	const guard = useAdGuardrails(AD_WALL_SURFACE);
	const [open, setOpen] = useState(false);
	const [wallIndex, setWallIndex] = useState(1);
	const openedRef = useRef(false);
	// This mount's page view, counted once toward the next repeat wall.
	const sinceRef = useRef<number | null | undefined>(undefined);

	const decided = ready && intensityState.ready && guard.ready;
	const eligible = decided && guard.blockReason !== "pro";

	useEffect(() => {
		if (!eligible) return;
		// H7/H8 v2: the landing page of a visit never walls on load (Google's
		// intrusive-interstitial rule), but a wall answering the visitor's own
		// apply click is user-initiated, so the `apply` arm may open there. The
		// eligible event on a landing therefore fires on the apply click, in
		// EVERY arm, so all arms keep one denominator.
		const landing = landingNow();
		const claimEligible = () => {
			if (!claimEligibleThisVisit()) return;
			posthog?.capture("ad_wall_eligible", {
				arm,
				page,
				locale,
				intensity,
				wall_index: 1,
			});
		};
		if (!landing) {
			claimEligible();
			if (sinceRef.current === undefined) {
				sinceRef.current = countPageViewSinceWall();
			}
		}
		const openOnce = (trigger: AdWallTrigger) => {
			if (openedRef.current) return;
			if (!wallOpensFor(arm, page, trigger)) return;
			const shown = wallsShownThisVisit();
			if (!wallDue(config, shown, sinceRef.current ?? null)) return;
			openedRef.current = true;
			markWallShown();
			setWallIndex((shown ?? 0) + 1);
			setOpen(true);
		};
		if (!landing) openOnce("mount");
		if (!landing && arm !== "apply") return;
		// The apply CTA opens the employer in a new tab before this event fires,
		// so the wall waits in our tab and never delays or blocks the apply.
		const onApply = () => {
			if (landing) claimEligible();
			openOnce("apply");
		};
		window.addEventListener(APPLY_CLICKED_EVENT, onApply);
		return () => window.removeEventListener(APPLY_CLICKED_EVENT, onApply);
	}, [eligible, arm, page, locale, posthog, intensity, config]);

	if (!open) return null;
	return (
		<AdWallDialog
			arm={arm}
			intensity={intensity}
			wallIndex={wallIndex}
			seconds={config.seconds}
			locale={locale}
			jobId={jobId}
			categoryId={categoryId}
			onClose={() => setOpen(false)}
		/>
	);
}
